import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { undefined } from 'zod';
import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import { CustomEventWithDetail, html, Html, View } from 'rune-ts';
import {
  ChangeOption,
  InputOption,
  SpoOption,
  SpoIntlOption,
  AnySpoOption,
  InputOptionInterface,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/InputOption.ts';
import klass from './Input_AddOption.module.scss';
import { StudioButton } from '@services/MarppleShop/shared/components/atoms/StudioButton/StudioButton.ts';
import { each, pipe, size, zipWithIndex, noop, isEmpty, head } from '@fxts/core';
import { InputOptionIntl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/InputOption_Intl.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { doIf } from '@services/MarppleShop/shared/util';
import { Alert } from '@services/MarppleShop/renderApp/components/cells/Alert/Alert.ts';

export class ApplyNewSpoItems extends CustomEventWithDetail<{
  spo_options: SpoOption[];
}> {}

interface InputAddOption extends InputEditor {
  value: SpoOption[];
  is_intl?: boolean;
  is_mobile?: boolean;
}

interface InputAddIntlOption extends InputEditor {
  value: SpoIntlOption[];
  is_intl?: boolean;
  is_mobile?: boolean;
}

const APPLY_BUTTON_LABEL = '적용하기';

export class Input_AddOption<T extends MODE> extends Input_Base<
  T extends typeof MODE.EDIT_TPL_INTL ? InputAddIntlOption : InputAddOption
> {
  private MAX_COUNT = 3;
  private spo_options = this.data.value.slice(0, this.MAX_COUNT);

  private applyOptionsButton = new StudioButton({
    custom_klass: `${klass.button} ${klass.wide}`,
    label: APPLY_BUTTON_LABEL,
    color: 'sky',
    size: 'free',
    is_disabled: !this.spo_options.length,
  });

  private optionViewMaps = this.getOptionViewMaps();

  protected override inputTemplate(): Html | View {
    return html`
      <div class="${klass.root}">
        <div class="${klass.option_container}">
          ${this.optionViewMaps.map((view_map) => {
            if (this.data.is_mobile) {
              const views = view_map.inputOptionView.getViews();
              return html`
                <div class="${klass.option_wrap} ${view_map.buttonAddView ? klass.has_add_button : ''}">
                  <div style="display: none">${view_map.inputOptionView}</div>
                  ${views.kr?.name}
                  ${doIf(() => html`${views.en?.name} ${views.jp?.name}`, !!this.data.is_intl)}
                  ${doIf(
                    () => html`
                      <div class="${klass.option_value_wrap}">
                        ${views.kr?.value}${view_map.buttonDeleteView} ${view_map.buttonAddView ?? ''}
                      </div>
                    `,
                    !this.data.is_intl,
                  )}
                  ${doIf(
                    () => html`
                      ${views.kr?.value} ${views.en?.value}
                      <div class="${klass.option_value_wrap}">
                        ${views.jp?.value}${view_map.buttonDeleteView} ${view_map.buttonAddView ?? ''}
                      </div>
                    `,
                    !!this.data.is_intl,
                  )}
                </div>
              `;
            }

            return html`
              <div class="${klass.option_wrap} ${view_map.buttonAddView ? klass.has_add_button : ''}">
                ${view_map.inputOptionView} ${view_map.buttonDeleteView} ${view_map.buttonAddView ?? ''}
              </div>
            `;
          })}
        </div>
        ${this.applyOptionsButton}
      </div>
    `;
  }

  override onRender() {
    /* 옵션명 및 옵션값 이벤트 */
    const changeOptionHandler = (optionView: InputOptionInterface) => {
      /* 삭제 및 추가버튼 활/비활성 */
      this.optionViewMaps.some(({ inputOptionView, buttonDeleteView, buttonAddView }, index) => {
        if (inputOptionView === optionView) {
          // 항목이 1개뿐인 첫번째 옵션이면서 비어있는 경우에만 삭제 버튼 disable
          buttonDeleteView.setDisable(
            index === 0 && this.optionViewMaps.length === 1 && optionView.isAllEmpty(),
          );
          buttonAddView?.setDisable(!optionView.isCompleted());
          return true;
        }

        return false;
      });

      this.setApplyOptionButtonDisable();
    };
    this.delegate(ChangeOption, InputOption, (_, optionView) => changeOptionHandler(optionView));
    this.delegate(ChangeOption, InputOptionIntl, (_, optionView) => changeOptionHandler(optionView));

    this.delegate('click', StudioButton, (_, buttonView) => {
      /* 적용하기 버튼 */
      if (buttonView.getLabel() === APPLY_BUTTON_LABEL) {
        this.applyOptions();
        return void 0;
      }

      this.optionViewMaps.some((view_map, index) => {
        /* 삭제 버튼 */
        if (view_map.buttonDeleteView === buttonView) {
          this.removeOptionInput(view_map.inputOptionView, view_map.buttonDeleteView, index);
          return true; // 찾았으니 중단
        }

        /* 추가 버튼 */
        if (view_map.buttonAddView === buttonView) {
          this.addOptionInput(index);
          return true; // 찾았으니 중단
        }

        return false; // 계속 찾기
      });
    });
  }

  private getSpoOptionInput(option: AnySpoOption, isShowAddButton: boolean) {
    return this.isInputAddIntlOption(this.data) && this.isSpoIntlOption(option)
      ? new InputOptionIntl({ ...option, isShowAddButton })
      : new InputOption(option);
  }

  private getDeleteButton(is_disabled: boolean) {
    return new StudioButton({
      custom_klass: klass.button,
      label: '삭제',
      color: 'line_gray',
      size: 'free',
      is_disabled,
    });
  }

  private getAddButton(is_disabled: boolean) {
    return new StudioButton({
      custom_klass: klass.button,
      label: '추가',
      color: 'line_gray',
      size: 'free',
      is_disabled,
    });
  }

  private removeOptionInput(optionView: InputOptionInterface, buttonDeleteView: StudioButton, index: number) {
    /* 항목이 1개뿐이고 첫번째 옵션에서 삭제버튼을 누르는 경우에만 초기화 */
    if (index === 0 && this.optionViewMaps.length === 1) {
      optionView.removeOptionInfo();
      buttonDeleteView.setDisable(true);
      this.applyOptionsButton.setDisable(false);
    } else {
      // 해당 인덱스의 옵션 뷰맵 제거
      this.optionViewMaps.splice(index, 1);

      // 마지막 옵션에 추가 버튼이 있어야 하는지 확인하고 추가
      const lastIndex = this.optionViewMaps.length - 1;
      if (lastIndex >= 0 && this.optionViewMaps.length < this.MAX_COUNT) {
        const lastOptionViewMap = this.optionViewMaps[lastIndex];
        // 기존 마지막 옵션에 추가 버튼이 없다면 추가
        if (lastOptionViewMap && !lastOptionViewMap.buttonAddView) {
          lastOptionViewMap.buttonAddView = this.getAddButton(
            !lastOptionViewMap.inputOptionView.isCompleted(),
          );
        }
      }

      this.setInputOptionLayout();
      this.redraw();
      this.setApplyOptionButtonDisable();
    }
  }

  private addOptionInput(currentIndex: number) {
    if (this.optionViewMaps.length >= this.MAX_COUNT) {
      return;
    }

    // 현재 옵션에서 추가 버튼 제거
    if (this.optionViewMaps[currentIndex]) {
      delete this.optionViewMaps[currentIndex].buttonAddView;
    }

    // 새로운 옵션 뷰맵 생성
    const newOptionViewMap = {
      inputOptionView: this.getSpoOptionInput(
        {
          name: '',
          name_en: '',
          name_jp: '',
          _: { spo_option_values: [] },
        },
        this.isShowAddButton(currentIndex),
      ),
      buttonDeleteView: this.getDeleteButton(false),
      // 추가 후에도 최대 개수 미만이면 추가 버튼 생성
      ...(this.optionViewMaps.length + 1 < this.MAX_COUNT ? { buttonAddView: this.getAddButton(true) } : {}),
    };

    // 새 옵션 추가
    this.optionViewMaps.push(newOptionViewMap);

    // 재렌더링 및 적용 버튼 상태 업데이트
    this.setInputOptionLayout();
    this.redraw();
    this.setApplyOptionButtonDisable();
  }

  private applyOptions() {
    this.spo_options = this.optionViewMaps.map(({ inputOptionView }) => inputOptionView.getOptionInfo());
    this.dispatchEvent(ApplyNewSpoItems, {
      bubbles: true,
      detail: {
        spo_options: pipe(
          this.spo_options,
          head,
          // 옵션이 1개이고 모든 값이 비워져 있는 경우 TOTAL_STOCK으로 전환하기 위해 빈배열을 전달
          (spo_option) => this.spo_options.length === 1 && spo_option && isEmpty(spo_option.name),
          (clear) => (clear ? [] : this.spo_options),
        ),
      },
    });
  }

  private setApplyOptionButtonDisable() {
    this.applyOptionsButton.setDisable(
      !this.optionViewMaps.every(({ inputOptionView }) => inputOptionView.isCompleted()),
    );
  }

  private setInputOptionLayout() {
    if (this.isInputAddIntlOption(this.data)) {
      pipe(
        this.optionViewMaps,
        zipWithIndex,
        each(([index, optionViewMap]) => {
          if (optionViewMap.inputOptionView instanceof InputOptionIntl) {
            optionViewMap.inputOptionView.setIsShowAddButton(
              index + 1 === size(this.optionViewMaps) && index + 1 < this.MAX_COUNT,
            );
          }
        }),
      );
    }
  }

  private getOptionViewMaps() {
    return this.spo_options.length
      ? this.spo_options.map((option, index) => {
          return {
            inputOptionView: this.getSpoOptionInput(option, this.isShowAddButton(index)),
            buttonDeleteView: this.getDeleteButton(false),
            // 가장 나중의 옵션이면서 최대 옵션 개수를 채우지 않은 상태에서만 추가 버튼을 그려준다.
            ...(this.isShowAddButton(index)
              ? {
                  buttonAddView: this.getAddButton(false),
                }
              : {}),
          };
        })
      : [
          {
            inputOptionView: this.getSpoOptionInput(
              {
                name: '',
                name_en: '',
                name_jp: '',
                _: { spo_option_values: [] },
              },
              true,
            ),
            buttonDeleteView: this.getDeleteButton(true),
            buttonAddView: this.getAddButton(true),
          },
        ];
  }

  private isShowAddButton(index: number) {
    return index === this.spo_options.length - 1 && this.spo_options.length < this.MAX_COUNT;
  }

  getName() {
    return this.data.name;
  }

  getValue() {
    return undefined;
  }

  setError(is_error: boolean, message: string) {
    if (this.data.is_intl) {
      const optionInput = this.subView(InputOptionIntl);
      optionInput &&
        pipe(
          optionInput.getTextEditors(),
          each((input) => {
            const condition = Boolean(input.getValue());
            if (!condition && is_error) input.setError(true);
            else input.setError(false);
          }),
        );
    } else {
      is_error &&
        Alert.open({
          title: message,
        }).then(noop);
    }
  }

  isInputAddIntlOption(data: InputAddOption | InputAddIntlOption): data is InputAddIntlOption {
    return Boolean(data.is_intl);
  }

  isSpoIntlOption(option: AnySpoOption): option is SpoIntlOption {
    return 'name_en' in option;
  }
}
